import {
  ChangeEvent,
  ReactNode,
  TextareaHTMLAttributes,
  forwardRef,
  useId,
  useState,
} from 'react'

import { FieldError } from 'react-hook-form'

import clsx from 'clsx'

import styles from './styles.module.scss'

const getTextareaValueLength = (value: unknown): number => {
  if (Array.isArray(value)) {
    return value.join('').length
  }

  return value ? String(value).length : 0
}

export interface TextareaWithLabelProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string
  leftIcon?: ReactNode
  rightIcon?: ReactNode
  error?: FieldError
  variant?: 'default' | 'secondary'
  showCounter?: boolean
}

export const TextareaWithLabel = forwardRef<
  HTMLTextAreaElement,
  TextareaWithLabelProps
>((props, ref) => {
  const {
    label,
    leftIcon,
    rightIcon,
    error,
    className,
    id,
    maxLength,
    onChange,
    value,
    defaultValue,
    showCounter,
    variant = 'default',
    placeholder = ' ',
    ...rest
  } = props

  const reactId = useId()
  const textareaId = id || `textarea-${reactId}`
  const isControlled = value !== undefined
  const [valueLength, setValueLength] = useState(
    getTextareaValueLength(defaultValue),
  )
  const counterValue = isControlled
    ? getTextareaValueLength(value)
    : valueLength
  const counterText = maxLength ? `${counterValue}/${maxLength}` : counterValue

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (!isControlled) {
      setValueLength(event.target.value.length)
    }

    onChange?.(event)
  }

  return (
    <div className={styles.wrapper}>
      <div
        className={clsx(
          styles.container,
          styles[`variant-${variant}`],
          error && styles.error,
          leftIcon && styles.hasLeftIcon,
        )}
      >
        {leftIcon && <div className={styles.leftIcon}>{leftIcon}</div>}

        <textarea
          ref={ref}
          id={textareaId}
          className={clsx(
            styles.textarea,
            leftIcon && styles.withLeftIcon,
            rightIcon && styles.withRightIcon,
            className,
          )}
          placeholder={placeholder}
          aria-invalid={!!error}
          maxLength={maxLength}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          {...rest}
        />

        <label
          htmlFor={textareaId}
          className={styles.label}
        >
          {label}
        </label>

        {rightIcon && <div className={styles.rightIcon}>{rightIcon}</div>}
      </div>

      {(error?.message || showCounter) && (
        <div className={styles.footer}>
          {error?.message && (
            <p className={styles.errorText}>{error.message}</p>
          )}

          {showCounter && (
            <span className={clsx(styles.counter, 'medium-14')}>
              {counterText}
            </span>
          )}
        </div>
      )}
    </div>
  )
})

TextareaWithLabel.displayName = 'TextareaWithLabel'
